import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpService } from '../core/http.service';
import { Observable } from 'rxjs';
import { AudienceQueryCountRequest, AudienceQueryCountResponse } from '../../models/services/audienceQueryCount';
import { CreateAudienceRequest } from '../../models/services/createAudience';
import { Audience, AudienceCategory, AudienceComment, TransferFile, AudiencesPage, AudiencesPageParameters } from '../../models/services/audience';
import { WamCustomSegment } from '../../models/services/wamCustomSegment';
import { UpdateAudienceRequest } from '../../models/services/updateAudience';
import { GlobalsService } from '../globals.service';
import { IdentifiersTypes } from '../../models/services/identifiersTypes.enum';
import { Tag } from '../../models/services/tag';
import { TargetPlatform } from 'src/app/models/services/audiences/target-platform';
import { TransfersAttributes } from 'src/app/models/services/transfersAttributes.enum';
import { TargetPlatforms } from 'src/app/models/services/targetPlatforms.enum';

@Injectable({
  providedIn: 'root'
})
export class AudiencesService {

  constructor(
    private httpService: HttpService,
    private globalsService: GlobalsService
  ) { }

  public getAudienceCounts(request: AudienceQueryCountRequest): Observable<AudienceQueryCountResponse> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/count`;
    return this.httpService.post(url, request, this.globalsService.getActiveCompany()?.id);
  }

  public getAudience(audienceId: number): Observable<Audience> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}`;
    return this.httpService.get(url, this.globalsService.getActiveCompany()?.id);
  }

  public getAudiences(): Observable<Audience[]> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences`;
    return this.httpService.get(url, this.globalsService.getActiveCompany()?.id, true);
  }

  public getAudiencesPage(pageParameters: AudiencesPageParameters): Observable<AudiencesPage> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/page`;
    return this.httpService.post(url, pageParameters, this.globalsService.getActiveCompany()?.id, true);
  }

  public createAudience(request: CreateAudienceRequest): Observable<void> {
      let url: string = `${environment.audiencesServicesApiBaseUri}/audiences`;    
      return this.httpService.postNoResponse(url, request, this.globalsService.getActiveCompany()?.id);  
  }

  public updateAudience(audienceId: number, request: UpdateAudienceRequest): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}`;
    return this.httpService.putNoResponse(url, request, this.globalsService.getActiveCompany()?.id);
  }

  public removeAudience(audienceId: number): Observable<Audience[]> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}`;
    return this.httpService.delete(url, this.globalsService.getActiveCompany()?.id);
  }

  public archiveAudience(audienceId: number): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/archive`;
    return this.httpService.postNoResponse(url, null, this.globalsService.getActiveCompany()?.id);
  }

  public refreshAudience(audienceId: number): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/refresh`;
    return this.httpService.postNoResponse(url, null, this.globalsService.getActiveCompany()?.id);
  }

  public sendAudience(audienceId: number, parameters: TransferFile): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/send`;
    return this.httpService.postNoResponse(url, parameters, this.globalsService.getActiveCompany()?.id);
  }
  
  public addComment(audienceId: number, audienceComment: AudienceComment): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/comments`;
    return this.httpService.postNoResponse(url, audienceComment, this.globalsService.getActiveCompany()?.id);
  }

  public removeComment(audienceId: number, audienceCommentId: number): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/comments/${audienceCommentId}`;
    return this.httpService.deleteNoResponse(url, this.globalsService.getActiveCompany()?.id);
  }

  public getAudiencesCategories(): Observable<AudienceCategory[]> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiencesCategories`;
    return this.httpService.get(url, this.globalsService.getActiveCompany()?.id);
  }

  public getWamCustomSegments(): Observable<WamCustomSegment[]> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/wam/customSegments`;
    return this.httpService.get(url, this.globalsService.getActiveCompany()?.id);
  }  

  public updateScheduling(audienceId: number, schedulingId: number, endDate?: Date): Observable<void> {
    let url: string;
    if (!!endDate)
      url = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/scheduling/${schedulingId}/${endDate.toISOString()}`;
    else
      url = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/scheduling/${schedulingId}`;
    
    return this.httpService.putNoResponse(url, null, this.globalsService.getActiveCompany()?.id);
  }    

  public moveAudience(audienceId: number, targetCompanyId: number): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/move/${targetCompanyId}`;
    return this.httpService.postNoResponse(url, null, this.globalsService.getActiveCompany()?.id);
  }    

  public getAllTags(): Observable<Tag[]> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/tags`;
    return this.httpService.get(url, this.globalsService.getActiveCompany()?.id, true);
  }  

  public updateTags(audienceId: number, tags: Tag[]): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/tags`;
    return this.httpService.putNoResponse(url, tags, this.globalsService.getActiveCompany()?.id);
  } 

  public downloadRational(audienceId: number): Observable<Blob> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/rational`;
    return this.httpService.getFile(url, this.globalsService.getActiveCompany()?.id);
  }  

  public getIdGraphTypeDescription = (identifierType: number) => {
    switch (identifierType) {
      case IdentifiersTypes.HashedEmail:
        return "Hashed emails";
      case IdentifiersTypes.WAMCookie:
        return "WAM cookies (encrypted)";
      case IdentifiersTypes.WAMCookie:
        return "WAM cookies";
      case IdentifiersTypes.MAID:
        return "MAIDs";
      case IdentifiersTypes.XandrCookie:
        return "Xandr cookies";
      case IdentifiersTypes.TTDCookie:
        return "TheTradeDesk cookies";
      case IdentifiersTypes.CTV:
        return "CTVs";           
      case -1:
        return "Home ids";     
      default:
        return "Unknown";
    };  
  }

  public getIdentifiersTypeSmallIcon = (identifierType: number) => {
    
    switch (identifierType) {
      case IdentifiersTypes.HashedEmail:
        return "/assets/images/identifiers/hashed-email.png";
      case IdentifiersTypes.WAMCookie:
        return "/assets/images/identifiers/weborama-cookie.png";
      case IdentifiersTypes.WAMCookie:
        return "/assets/images/identifiers/weborama-cookie.png";
      case IdentifiersTypes.MAID:
        return "/assets/images/identifiers/maid.png";
      case IdentifiersTypes.XandrCookie:
        return "/assets/images/identifiers/xandr-cookie.png";
      case IdentifiersTypes.TTDCookie:
        return "/assets/images/identifiers/ttd-cookie.png";
      case IdentifiersTypes.CTV:
        return "/assets/images/identifiers/ctv.png";
      default:
        return "/assets/images/identifiers/home.png";
    };  
  }

}
